"use client";

import {
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import {
  ChevronDown,
  ChevronUp,
  Copy,
  GripVertical,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { CaseLayout, InboxFieldResponse } from "@/generated";
import { cn } from "@/lib/utils";
import { Button } from "@/components/shadcn/button";
import { Input } from "@/components/shadcn/input";
import { FieldWidget } from "@/components/Fields";
import { TruncatedTextWithTooltip } from "@/components/TruncatedTextWithTooltip";
import { LayoutSectionCard } from "@/components/CaseManagement/LayoutArranged";
import {
  addSection,
  applyFieldDrop,
  clampPairSplit,
  computeAutoLayout,
  fromApi,
  GRID_COLS,
  groupRows,
  heightLookup,
  MIN_CELL_W,
  minWidthLookup,
  moveSection,
  normalizeSectionRows,
  reconcileLayout,
  removeSection,
  renameSection,
  resizeRowPair,
  sectionTitleExists,
  type EditableLayout,
  type FieldDropTarget,
  type ReconciledCell,
  type ReconciledSection,
} from "@/components/CaseManagement/caseLayout";

const GAP = 16;
const DRAG_THRESHOLD = 5;
// A target switch shifts the layout under the pointer; requiring real pointer
// travel before the next switch breaks preview-feedback oscillation.
const TARGET_SWITCH_EPS = 12;

type DropRef = { sectionIndex: number; drop: FieldDropTarget } | null;

const targetsEqual = (a: DropRef, b: DropRef): boolean => {
  if (!a || !b) return a === b;
  if (a.sectionIndex !== b.sectionIndex || a.drop.kind !== b.drop.kind) {
    return false;
  }
  const d1 = a.drop;
  const d2 = b.drop;
  if (d1.kind === "merge" && d2.kind === "merge") {
    return d1.rowY === d2.rowY && d1.index === d2.index;
  }
  if (d1.kind === "before-row" && d2.kind === "before-row") {
    return d1.rowY === d2.rowY;
  }
  if (d1.kind === "between-sections" && d2.kind === "between-sections") {
    return d1.index === d2.index;
  }
  return true;
};

const toEditable = (sections: ReconciledSection[]): EditableLayout => ({
  version: 1,
  sections: sections.map((s) => ({
    title: s.title,
    cells: s.cells.map((c) => ({
      field_id: c.field.id,
      x: c.x,
      y: c.y,
      w: c.w,
    })),
  })),
});

/** Build the editor's starting draft: the stored layout reconciled against
 * the current fields, or an auto-placement seed when no layout exists. */
export const seedLayoutDraft = (
  caseLayout: CaseLayout | null | undefined,
  visibleFields: InboxFieldResponse[]
): EditableLayout => {
  const existing = fromApi(caseLayout);
  let draft = existing
    ? toEditable(reconcileLayout(existing, visibleFields, true))
    : computeAutoLayout(visibleFields);
  // Rows always span the full grid; stored layouts from before this rule
  // (or with since-deleted fields) get snapped on entry.
  const heightOf = heightLookup(visibleFields);
  draft.sections.forEach((_, index) => {
    draft = normalizeSectionRows(draft, index, heightOf);
  });
  return draft;
};

const mockScalar = (type: string, options: unknown, seed: number): unknown => {
  switch (type) {
    case "text":
      return seed === 0 ? "Sample text" : `Sample text ${seed + 1}`;
    case "textarea":
      return "Sample description text so you can preview how longer content sits in this arrangement.";
    case "number":
      return 42 + seed;
    case "date":
      return seed === 0 ? "2026-01-15" : "2026-02-03";
    case "boolean":
      return seed % 2 === 0;
    case "select": {
      if (Array.isArray(options) && typeof options[0] === "string") {
        return (options as string[])[seed % options.length];
      }
      return null;
    }
    case "email":
      return seed === 0 ? "jane.doe@example.com" : "sam.smith@example.com";
    case "phone":
      return "+1 555 0100";
    case "url":
      return "https://example.com";
    default:
      return null;
  }
};

export const mockValueForField = (field: InboxFieldResponse): unknown => {
  if (field.type === "table") {
    const columns = Array.isArray(field.options)
      ? (field.options as unknown[]).filter(
          (c): c is { name: string; type?: string; options?: unknown } =>
            typeof c === "object" && c !== null && "name" in c
        )
      : [];
    if (columns.length === 0) return [];
    return [0, 1].map((seed) =>
      Object.fromEntries(
        columns.map((col) => [
          col.name,
          mockScalar(col.type ?? "text", col.options, seed),
        ])
      )
    );
  }
  return mockScalar(field.type, field.options, 0);
};

const FieldCellCard = memo(function FieldCellCard({
  field,
  previewValue,
  gestureActive,
  onEditField,
  onDuplicateField,
}: {
  field: InboxFieldResponse;
  previewValue: unknown;
  gestureActive: boolean;
  onEditField?: (field: InboxFieldResponse) => void;
  onDuplicateField?: (field: InboxFieldResponse) => void;
}) {
  return (
    <div
      className={cn(
        "group/cell h-full w-full overflow-hidden rounded-lg border border-border bg-card px-3 py-2 flex flex-col transition-shadow",
        !gestureActive &&
          "cursor-grab active:cursor-grabbing hover:shadow-[0_0_0_2px_#93c5fd,0_0_0_6px_rgba(191,219,254,0.55)]"
      )}
    >
      <div className="flex items-center gap-1.5 min-w-0 shrink-0">
        <GripVertical className="w-3.5 h-3.5 shrink-0 text-muted-foreground/40" />
        <div className="min-w-0 flex-1 text-sm font-medium text-primary">
          <TruncatedTextWithTooltip>{field.name}</TruncatedTextWithTooltip>
        </div>
        {onEditField && (
          <button
            type="button"
            title="Edit property"
            aria-label={`Edit property ${field.name}`}
            className="layout-no-drag shrink-0 cursor-pointer rounded p-1 text-muted-foreground opacity-0 transition-opacity group-hover/cell:opacity-100 hover:bg-muted"
            onClick={() => onEditField(field)}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
        {onDuplicateField && (
          <button
            type="button"
            title="Duplicate property"
            aria-label={`Duplicate property ${field.name}`}
            className="layout-no-drag shrink-0 cursor-pointer rounded p-1 text-muted-foreground opacity-0 transition-opacity group-hover/cell:opacity-100 hover:bg-muted"
            onClick={() => onDuplicateField(field)}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="mt-1 flex-1 min-h-0 pointer-events-none select-none">
        <FieldWidget
          field={field}
          value={previewValue}
          onChange={() => undefined}
          readOnly
          compact
        />
      </div>
    </div>
  );
});

type DragSession = {
  fieldId: string;
  fieldName: string;
  startX: number;
  startY: number;
  started: boolean;
  pointer: { x: number; y: number };
  target: { sectionIndex: number; drop: FieldDropTarget } | null;
};

type ResizeSession = {
  sectionIndex: number;
  leftId: string;
  rightId: string;
  pairTotal: number;
  leftW: number;
  leftEdgePx: number;
  colUnit: number;
};

export const CaseLayoutEditor = ({
  draft,
  onChange,
  fields,
  previewValues,
  onAddField,
  onAddFieldToSection,
  onEditField,
  onDuplicateField,
  allowGroups = true,
}: {
  draft: EditableLayout;
  onChange: (draft: EditableLayout) => void;
  fields: InboxFieldResponse[];
  previewValues: Record<string, unknown>;
  onAddField?: () => void;
  onAddFieldToSection?: (sectionTitle: string) => void;
  onEditField?: (field: InboxFieldResponse) => void;
  onDuplicateField?: (field: InboxFieldResponse) => void;
  /** Group sections make no sense in some hosts (task layouts): hides the
   * Add group affordance. */
  allowGroups?: boolean;
}) => {
  const heightOf = useMemo(() => heightLookup(fields), [fields]);
  const minOf = useMemo(() => minWidthLookup(fields), [fields]);
  const fieldsById = useMemo(
    () => new Map(fields.map((f) => [f.id, f])),
    [fields]
  );
  const [drag, setDrag] = useState<DragSession | null>(null);
  const [resize, setResize] = useState<ResizeSession | null>(null);
  // Mirrors the drag state synchronously: pointerup can fire before React
  // re-renders the last pointermove, and the drop must not lag one step.
  const dragLive = useRef<DragSession | null>(null);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scrollParentRef = useRef<HTMLElement | null>(null);
  const lastSwitchAt = useRef<{ x: number; y: number } | null>(null);
  const skipNextFlip = useRef(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const cellRects = useRef(new Map<string, { left: number; top: number }>());

  // Preview the exact drop result live; the dragged field renders as a slot.
  // Depends on the target IDENTITY (stable between switches), never the
  // pointer position — otherwise every mousemove rebuilds the preview and
  // retriggers layout work/animations.
  const dragStarted = drag?.started ?? false;
  const dragFieldId = drag?.fieldId;
  const dragTarget = drag?.target ?? null;
  const previewLayout = useMemo(() => {
    if (!dragStarted || !dragTarget || !dragFieldId) return draft;
    return applyFieldDrop(
      draft,
      dragTarget.sectionIndex,
      dragFieldId,
      dragTarget.drop,
      heightOf
    );
  }, [draft, dragStarted, dragFieldId, dragTarget, heightOf]);

  const dragging = Boolean(drag?.started);
  const displayed = dragging ? previewLayout : draft;
  sectionRefs.current.length = displayed.sections.length;

  // Row identities for drop targets are anchored on member field ids and
  // resolved against the draft *without* the dragged cell — the same rows
  // applyFieldDrop will see — so preview shifts can't skew the target.
  const anchorRowY = useMemo(() => {
    if (!drag) return new Map<string, number>();
    const map = new Map<string, number>();
    for (const section of draft.sections) {
      const rows = groupRows(
        section.cells.filter((c) => c.field_id !== drag.fieldId)
      );
      for (const row of rows) {
        for (const cell of row) map.set(cell.field_id, row[0].y);
      }
    }
    return map;
  }, [draft, drag]);

  const latest = useRef({
    draft,
    previewLayout,
    drag,
    resize,
    heightOf,
    minOf,
  });
  latest.current = { draft, previewLayout, drag, resize, heightOf, minOf };

  const decideTarget = (
    px: number,
    py: number
  ): { sectionIndex: number; drop: FieldDropTarget } | null => {
    const { drag: session } = latest.current;
    if (!session) return null;
    // Pointer resting on the previewed slot keeps the current target stable.
    const slot = document.querySelector('[data-drag-slot="true"]');
    if (slot) {
      const r = slot.getBoundingClientRect();
      if (px >= r.left && px <= r.right && py >= r.top && py <= r.bottom) {
        return session.target;
      }
    }
    const draggedTall = latest.current.heightOf(session.fieldId) > 1;
    const draftOwnerOf = (memberId: string): number =>
      latest.current.draft.sections.findIndex((sec) =>
        sec.cells.some((c) => c.field_id === memberId)
      );
    const sectionMember = (el: HTMLElement): string | undefined =>
      [...el.querySelectorAll<HTMLElement>("[data-cell-id]")].find(
        (c) => c.dataset.cellId !== session.fieldId
      )?.dataset.cellId;
    // Sections are rendered from the PREVIEW, whose indices can differ from
    // the draft's (a drop can insert/remove loose sections); the draft
    // insertion point at a display boundary is the owner of the next real
    // member below it.
    const draftInsertionAt = (displayIndex: number): number => {
      for (let j = displayIndex; j < sectionRefs.current.length; j++) {
        const jel = sectionRefs.current[j];
        if (!jel) continue;
        const member = sectionMember(jel);
        if (member) {
          const owner = draftOwnerOf(member);
          if (owner !== -1) return owner;
        }
      }
      return latest.current.draft.sections.length;
    };
    const rects = sectionRefs.current.map(
      (el) => el?.getBoundingClientRect() ?? null
    );
    // First section whose bottom is below the pointer; past the last one,
    // fall back to the last section.
    let sectionIndex = -1;
    for (let i = 0; i < rects.length; i++) {
      const r = rects[i];
      if (r && py <= r.bottom) {
        sectionIndex = i;
        break;
      }
    }
    if (sectionIndex === -1) {
      // Below every section: a loose row after the last one.
      return {
        sectionIndex: 0,
        drop: {
          kind: "between-sections",
          index: latest.current.draft.sections.length,
        },
      };
    }
    // Pointer in the gap above this section (between sections, or over a
    // group's title) means a loose row at that boundary — dropping between
    // two groups lands the field outside both.
    const sectionRect = rects[sectionIndex]!;
    if (py < sectionRect.top) {
      return {
        sectionIndex: 0,
        drop: {
          kind: "between-sections",
          index: draftInsertionAt(sectionIndex),
        },
      };
    }
    const el = sectionRefs.current[sectionIndex]!;

    const rowEls = [...el.querySelectorAll<HTMLElement>("[data-row]")];
    for (const rowEl of rowEls) {
      const members = [
        ...rowEl.querySelectorAll<HTMLElement>("[data-cell-id]"),
      ].filter((c) => c.dataset.cellId !== session.fieldId);
      const anchor = members[0]?.dataset.cellId;
      if (!anchor) continue; // the slot's own row
      const rowY = anchorRowY.get(anchor);
      if (rowY === undefined) continue;
      const ownerIndex = draftOwnerOf(anchor);
      if (ownerIndex === -1) continue;
      const r = rowEl.getBoundingClientRect();
      if (py < r.top + r.height * 0.33) {
        return { sectionIndex: ownerIndex, drop: { kind: "before-row", rowY } };
      }
      if (py <= r.top + r.height * 0.72) {
        const canMerge =
          !draggedTall &&
          members.length < GRID_COLS / MIN_CELL_W &&
          members.every((m) => m.dataset.cellH === "1");
        if (canMerge) {
          const index = members.filter((m) => {
            const mr = m.getBoundingClientRect();
            return mr.left + mr.width / 2 <= px;
          }).length;
          return {
            sectionIndex: ownerIndex,
            drop: { kind: "merge", rowY, index },
          };
        }
        if (py <= r.top + r.height / 2) {
          return {
            sectionIndex: ownerIndex,
            drop: { kind: "before-row", rowY },
          };
        }
        return (
          targetBelowRow(rowEls, rowEl, session.fieldId, draftOwnerOf) ??
          session.target
        );
      }
    }
    const endMember = sectionMember(el);
    const endOwner = endMember ? draftOwnerOf(endMember) : -1;
    if (endOwner === -1) return session.target;
    return { sectionIndex: endOwner, drop: { kind: "end" } };
  };

  const targetBelowRow = (
    rowEls: HTMLElement[],
    rowEl: HTMLElement,
    draggedId: string,
    draftOwnerOf: (memberId: string) => number
  ): { sectionIndex: number; drop: FieldDropTarget } | null => {
    const index = rowEls.indexOf(rowEl);
    for (let i = index + 1; i < rowEls.length; i++) {
      const anchor = [
        ...rowEls[i].querySelectorAll<HTMLElement>("[data-cell-id]"),
      ].find((c) => c.dataset.cellId !== draggedId)?.dataset.cellId;
      const rowY = anchor ? anchorRowY.get(anchor) : undefined;
      const owner = anchor ? draftOwnerOf(anchor) : -1;
      if (rowY !== undefined && owner !== -1) {
        return { sectionIndex: owner, drop: { kind: "before-row", rowY } };
      }
    }
    return null;
  };

  const startDrag = (e: ReactPointerEvent, field: InboxFieldResponse) => {
    if (e.button !== 0) return;
    const targetEl = e.target as HTMLElement;
    if (targetEl.closest(".layout-no-drag, [data-resize-handle]")) return;
    let parent: HTMLElement | null = (e.currentTarget as HTMLElement)
      .parentElement;
    while (
      parent &&
      !(
        parent.scrollHeight > parent.clientHeight &&
        /auto|scroll/.test(getComputedStyle(parent).overflowY)
      )
    ) {
      parent = parent.parentElement;
    }
    scrollParentRef.current = parent;
    const session: DragSession = {
      fieldId: field.id,
      fieldName: field.name,
      startX: e.clientX,
      startY: e.clientY,
      started: false,
      pointer: { x: e.clientX, y: e.clientY },
      target: null,
    };
    dragLive.current = session;
    lastSwitchAt.current = null;
    setDrag(session);
  };

  const startResize = (
    e: ReactPointerEvent,
    sectionIndex: number,
    left: ReconciledCell,
    right: ReconciledCell
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const rowEl = (e.currentTarget as HTMLElement).closest("[data-row]");
    if (!rowEl) return;
    const rect = rowEl.getBoundingClientRect();
    const rowCellCount = rowEl.querySelectorAll("[data-cell-id]").length || 1;
    const colUnit = (rect.width - GAP * (rowCellCount - 1)) / GRID_COLS;
    const leftIndex =
      [...rowEl.querySelectorAll<HTMLElement>("[data-cell-id]")].findIndex(
        (c) => c.dataset.cellId === left.field.id
      ) ?? 0;
    setResize({
      sectionIndex,
      leftId: left.field.id,
      rightId: right.field.id,
      pairTotal: left.w + right.w,
      leftW: left.w,
      leftEdgePx: rect.left + left.x * colUnit + leftIndex * GAP,
      colUnit,
    });
  };

  useEffect(() => {
    if (!drag && !resize) return;
    const onMove = (e: PointerEvent) => {
      const state = latest.current;
      if (e.buttons === 0) {
        onUp();
        return;
      }
      if (state.resize) {
        e.preventDefault();
        const raw = Math.round(
          (e.clientX - state.resize.leftEdgePx) / state.resize.colUnit
        );
        const leftW = clampPairSplit(
          raw,
          state.resize.pairTotal,
          state.minOf(state.resize.leftId),
          state.minOf(state.resize.rightId)
        );
        if (leftW !== state.resize.leftW) {
          setResize({ ...state.resize, leftW });
        }
        return;
      }
      if (!state.drag) return;
      e.preventDefault();
      const dist = Math.hypot(
        e.clientX - state.drag.startX,
        e.clientY - state.drag.startY
      );
      if (!state.drag.started && dist < DRAG_THRESHOLD) return;
      const scroller = scrollParentRef.current;
      if (scroller) {
        const sr = scroller.getBoundingClientRect();
        if (e.clientY < sr.top + 56) scroller.scrollTop -= 16;
        else if (e.clientY > sr.bottom - 56) scroller.scrollTop += 16;
      }
      const candidate = decideTarget(e.clientX, e.clientY);
      let target = state.drag.target;
      if (!targetsEqual(candidate, target)) {
        const ref = lastSwitchAt.current;
        const travelled = ref
          ? Math.hypot(e.clientX - ref.x, e.clientY - ref.y)
          : Infinity;
        if (target === null || travelled >= TARGET_SWITCH_EPS) {
          target = candidate;
          lastSwitchAt.current = { x: e.clientX, y: e.clientY };
        }
      }
      const next = {
        ...state.drag,
        started: true,
        pointer: { x: e.clientX, y: e.clientY },
        target,
      };
      dragLive.current = next;
      setDrag(next);
    };
    function onUp() {
      const state = latest.current;
      if (state.resize) {
        // Cells already tracked the divider live; the commit must not FLIP
        // them from their stale pre-resize baseline.
        skipNextFlip.current = true;
        onChange(
          resizeRowPair(
            state.draft,
            state.resize.sectionIndex,
            state.resize.leftId,
            state.resize.leftW,
            state.minOf
          )
        );
        setResize(null);
        return;
      }
      const session = dragLive.current ?? state.drag;
      if (session) {
        if (session.started && session.target) {
          onChange(
            applyFieldDrop(
              state.draft,
              session.target.sectionIndex,
              session.fieldId,
              session.target.drop,
              state.heightOf
            )
          );
        }
        dragLive.current = null;
        lastSwitchAt.current = null;
        setDrag(null);
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      dragLive.current = null;
      lastSwitchAt.current = null;
      setDrag(null);
      setResize(null);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(drag), Boolean(resize)]);

  // FLIP: when the displayed layout changes, glide cells from their previous
  // position to the new one. Runs ONLY on layout changes (not pointer moves);
  // an in-flight glide hands off smoothly on interruption. Positions are
  // relative to the editor root so scrolling never reads as movement.
  const displayedRef = useRef(displayed);
  useLayoutEffect(() => {
    if (displayedRef.current === displayed) return;
    displayedRef.current = displayed;
    try {
      const root = rootRef.current;
      if (!root) return;
      const rootRect = root.getBoundingClientRect();
      const previous = cellRects.current;
      const measured = new Map<string, { left: number; top: number }>();
      root.querySelectorAll<HTMLElement>("[data-cell-id]").forEach((el) => {
        const id = el.dataset.cellId;
        if (!id) return;
        const animations = el.getAnimations();
        const visualRect = el.getBoundingClientRect();
        animations.forEach((a) => a.cancel());
        const rect = animations.length
          ? el.getBoundingClientRect()
          : visualRect;
        const pos = {
          left: rect.left - rootRect.left,
          top: rect.top - rootRect.top,
        };
        measured.set(id, pos);
        if (resize || skipNextFlip.current) return;
        // The dragged slot belongs under the pointer — teleporting it is
        // correct, and its long glide is the main out-of-bounds painter.
        if (id === latest.current.drag?.fieldId) return;
        const from = animations.length
          ? {
              left: visualRect.left - rootRect.left,
              top: visualRect.top - rootRect.top,
            }
          : previous.get(id);
        if (!from) return;
        const dx = from.left - pos.left;
        const dy = from.top - pos.top;
        if (Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
        el.animate(
          [
            { transform: `translate(${dx}px, ${dy}px)` },
            { transform: "translate(0, 0)" },
          ],
          { duration: 160, easing: "ease" }
        );
      });
      cellRects.current = measured;
      skipNextFlip.current = false;
    } catch {
      // Animation polish must never take the editor down.
    }
  });

  useEffect(() => {
    if (resize) document.body.style.cursor = "ew-resize";
    else if (dragging) document.body.style.cursor = "grabbing";
    else document.body.style.cursor = "";
    return () => {
      document.body.style.cursor = "";
    };
  }, [Boolean(resize), dragging]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddSection = () => {
    let title = "New group";
    let suffix = 2;
    while (sectionTitleExists(draft, title)) title = `New group ${suffix++}`;
    onChange(addSection(draft, title));
  };

  const renderSection = (
    section: (typeof displayed.sections)[number],
    draftIndex: number
  ) => {
    const rows = groupRows(section.cells);
    if (rows.length === 0) {
      return (
        <p className="py-4 text-center text-xs text-muted-foreground">
          Drag properties here
        </p>
      );
    }
    return (
      <div className="flex flex-col" style={{ gap: GAP }}>
        {rows.map((rowCells) => {
          const resolved = rowCells.flatMap((cell) => {
            const field = fieldsById.get(cell.field_id);
            return field
              ? [{ field, ...cell, h: heightOf(cell.field_id) }]
              : [];
          });
          if (resolved.length === 0) return null;
          const n = resolved.length;
          const rowKey = resolved.map((c) => c.field.id).join(":");
          const inResizeRow =
            resize &&
            resize.sectionIndex === draftIndex &&
            resolved.some((c) => c.field.id === resize.leftId);
          return (
            <div
              key={rowKey}
              data-row
              className="flex items-stretch"
              style={{ gap: GAP }}
            >
              {resolved.map((cell, i) => {
                let w = cell.w;
                if (inResizeRow && resize) {
                  if (cell.field.id === resize.leftId) w = resize.leftW;
                  if (cell.field.id === resize.rightId) {
                    w = resize.pairTotal - resize.leftW;
                  }
                }
                const isSlot = dragging && cell.field.id === drag?.fieldId;
                const nextCell = resolved[i + 1];
                const pairResizable =
                  !dragging &&
                  nextCell &&
                  cell.h === 1 &&
                  nextCell.h === 1 &&
                  !isSlot &&
                  nextCell.field.id !== drag?.fieldId;
                return (
                  <div
                    key={cell.field.id}
                    data-cell-id={cell.field.id}
                    data-cell-h={cell.h}
                    data-drag-slot={isSlot || undefined}
                    className="relative min-w-0"
                    style={{
                      width: `calc((100% - ${(n - 1) * GAP}px) * ${w / GRID_COLS})`,
                      transition: inResizeRow ? "none" : "width 120ms ease",
                    }}
                    onPointerDown={(e) => startDrag(e, cell.field)}
                  >
                    <div
                      className={cn(
                        "h-full rounded-lg",
                        isSlot &&
                          "border-[1.5px] border-dashed border-primary/45 bg-[rgba(120,124,140,0.10)]"
                      )}
                    >
                      <div className={cn("h-full", isSlot && "opacity-35")}>
                        <FieldCellCard
                          field={cell.field}
                          previewValue={previewValues[cell.field.id]}
                          gestureActive={dragging || Boolean(resize)}
                          onEditField={onEditField}
                          onDuplicateField={onDuplicateField}
                        />
                      </div>
                    </div>
                    {pairResizable && (
                      <div
                        data-resize-handle
                        className="group/handle absolute top-0 -right-[14px] z-10 h-full w-[24px] cursor-ew-resize"
                        onPointerDown={(e) =>
                          startResize(e, draftIndex, cell, nextCell)
                        }
                      >
                        <span
                          className={cn(
                            "absolute left-1/2 top-1/2 h-[26px] w-[4px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-300 transition-opacity",
                            inResizeRow && resize?.leftId === cell.field.id
                              ? "opacity-100"
                              : "opacity-0 group-hover/handle:opacity-100"
                          )}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      ref={rootRef}
      // overflow-clip: FLIP glides paint outside the content box when rows
      // collapse; without the clip the host's scroll container grows a
      // scrollbar for a split second. The p-2/-m-2 pair keeps card halos
      // and handles inside the clip box.
      className={cn(
        "flex flex-col gap-4 overflow-clip p-2 -m-2",
        (drag || resize) && "select-none"
      )}
    >
      <div className="flex flex-col" style={{ gap: GAP }}>
        {displayed.sections.map((displaySection, displayIndex) => {
          // Actions target the DRAFT section; during a drag preview the
          // indices can differ, so resolve via title (groups are unique)
          // or a member field. -1 = preview-only loose section (no actions).
          const firstMember = displaySection.cells.find(
            (c) => c.field_id !== drag?.fieldId
          )?.field_id;
          const draftIndex =
            displaySection.title !== null
              ? draft.sections.findIndex(
                  (sec) => sec.title === displaySection.title
                )
              : firstMember
                ? draft.sections.findIndex((sec) =>
                    sec.cells.some((c) => c.field_id === firstMember)
                  )
                : -1;
          const body = (
            <div
              ref={(el) => {
                sectionRefs.current[displayIndex] = el;
              }}
              data-section-index={displayIndex}
            >
              {renderSection(displaySection, draftIndex)}
            </div>
          );
          if (displaySection.title === null) {
            const empty = displaySection.cells.length === 0;
            return (
              <div
                key={`untitled-${displayIndex}`}
                className={cn(empty && "hidden")}
              >
                {body}
              </div>
            );
          }
          return (
            <div key={`titled-${displaySection.title}`} className="space-y-1">
              <div className="flex items-center gap-2">
                <Input
                  key={displaySection.title}
                  defaultValue={displaySection.title}
                  className="h-8 w-64 text-sm font-medium"
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (
                      value &&
                      value !== displaySection.title &&
                      draftIndex !== -1
                    ) {
                      onChange(renameSection(draft, draftIndex, value));
                    }
                  }}
                />
                {onAddFieldToSection && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground"
                    title="Add property to this group"
                    aria-label="Add property to this group"
                    onClick={() => onAddFieldToSection(displaySection.title!)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground"
                  title="Move group up"
                  aria-label="Move group up"
                  disabled={draftIndex <= 0}
                  onClick={() => onChange(moveSection(draft, draftIndex, -1))}
                >
                  <ChevronUp className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground"
                  title="Move group down"
                  aria-label="Move group down"
                  disabled={
                    draftIndex === -1 ||
                    draftIndex === draft.sections.length - 1
                  }
                  onClick={() => onChange(moveSection(draft, draftIndex, 1))}
                >
                  <ChevronDown className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground"
                  title="Delete group"
                  aria-label="Delete group"
                  disabled={draftIndex === -1}
                  onClick={() =>
                    onChange(removeSection(draft, draftIndex, heightOf))
                  }
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <LayoutSectionCard
                className={cn(
                  "px-4",
                  displaySection.cells.length === 0 ? "py-1" : "py-3"
                )}
              >
                {body}
              </LayoutSectionCard>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        {onAddField && (
          <Button variant="outline" onClick={onAddField}>
            <Plus />
            Add property
          </Button>
        )}
        {allowGroups && (
          <Button variant="outline" onClick={handleAddSection}>
            <Plus />
            Add group
          </Button>
        )}
      </div>

      {dragging &&
        drag &&
        // Portaled to <body>: a transformed ancestor (e.g. a centered
        // dialog) would otherwise turn position:fixed into
        // ancestor-relative coordinates and strand the chip.
        createPortal(
          <div
            className="pointer-events-none fixed z-100 rounded-md border bg-white px-3 py-1.5 text-sm font-medium shadow-md"
            style={{ left: drag.pointer.x + 12, top: drag.pointer.y + 12 }}
          >
            {drag.fieldName}
          </div>,
          document.body
        )}
    </div>
  );
};
